import type { CancelLockSource } from '@/infrastructure/FulfillmentAdminClient';
import { kstMonthDayMinute } from '@/utils/fulfillmentTime';

/** The lock as the screens read it off an order header or a list row; both fields are absent on an older server. */
export interface CancelLockFields {
  cancelLockedAt?: string | null;
  cancelLockSource?: CancelLockSource | null;
}

/** A lock the screen can show: when (already on the KST clock) and what set it, in plain words. */
export interface CancelLock {
  /** `10/18 14:05 KST`. */
  at: string;
  /** The lock's instant as the server sent it, so a screen can set it beside when the customer asked (SSD-7643). */
  iso: string;
  /** `First purchase`, or null when the server named no source the screen knows. */
  source: string | null;
}

const SOURCE_LABELS: Readonly<Record<CancelLockSource, string>> = {
  FIRST_PURCHASE: 'First purchase',
  OUTBOUND: 'Outbound',
  BACKFILL: 'Backfill',
};

/** Plain words for what locked the order; an unknown code reads as nothing rather than as a raw enum. */
export const cancelLockSourceLabel = (source?: string | null): string | null =>
  source && source in SOURCE_LABELS ? SOURCE_LABELS[source as CancelLockSource] : null;

/** The lock to show, or null when the order is not locked (or the server predates the lock). */
export const cancelLockOf = (fields?: CancelLockFields | null): CancelLock | null => {
  const iso = fields?.cancelLockedAt;
  const at = kstMonthDayMinute(iso);
  return at === null || !iso ? null : { at, iso, source: cancelLockSourceLabel(fields?.cancelLockSource) };
};

/** `Cancel locked at 10/18 14:05 KST (First purchase)` — the one sentence the badge, its tooltip and the dialog share. */
export const describeCancelLock = (lock: CancelLock): string =>
  `Cancel locked at ${lock.at}${lock.source ? ` (${lock.source})` : ''}`;
